import type { AnyEventPayload } from "@workspace/dto/event-registry/types";
import type { NotificationType } from "@workspace/dto/notification/schema";
import type { z } from "zod";

export type RecipientInfo = {
  userId: string;
  email?: string | null;
  name?: string | null;
};

export type EmailRecipientInfo = {
  userId: string;
  email: string;
  name: string;
};

export function toEmailRecipient(
  recipient: RecipientInfo
): EmailRecipientInfo | null {
  if (!recipient.email) {
    return null;
  }

  return {
    userId: recipient.userId,
    email: recipient.email,
    name: recipient.name?.trim() || "there",
  };
}

export type HandlerCtx = {
  organizationId?: string;
};

export function requireOrganizationId(ctx: HandlerCtx): string {
  if (!ctx.organizationId) {
    throw new Error(
      "An organization-scoped notification requires an organizationId"
    );
  }
  return ctx.organizationId;
}

export function resolveWorkplaceName(name?: string | null): string {
  return name?.trim() || "the employer";
}

export type EmailHandlerCtx = HandlerCtx & {
  baseUrl: string;
  unsubscribeUrl: string;
};

export type NotificationPayload = {
  metadata: Record<string, unknown>;
  links?: {
    href: string;
    label?: string;
  };
  priority?: number;
  groupId?: string;
};

export type EmailEvent = AnyEventPayload;

export type NotificationIcon = "message" | "interview" | "application";

export type NotificationTypeHandler = {
  type: NotificationType;
  icon: NotificationIcon;
  title: (metadata: Record<string, unknown>) => string;
  description: (metadata: Record<string, unknown>) => string;
  schema: z.ZodSchema;
  buildNotificationPayload: (
    data: Record<string, unknown>,
    ctx: HandlerCtx
  ) => NotificationPayload;
  buildEmailPayload?: (
    data: Record<string, unknown>,
    recipient: EmailRecipientInfo,
    ctx: EmailHandlerCtx
  ) => EmailEvent;
};

type EmailNotificationTypeHandler = NotificationTypeHandler & {
  buildEmailPayload: (
    data: Record<string, unknown>,
    recipient: EmailRecipientInfo,
    ctx: EmailHandlerCtx
  ) => EmailEvent;
};

type NotificationHandlerDefinition<TSchema extends z.ZodType> = Omit<
  NotificationTypeHandler,
  "schema" | "buildNotificationPayload" | "buildEmailPayload"
> & {
  schema: TSchema;
  buildNotificationPayload?: (
    data: z.output<TSchema>,
    ctx: HandlerCtx
  ) => NotificationPayload;
};

export function defineNotificationHandler<TSchema extends z.ZodType>(
  definition: NotificationHandlerDefinition<TSchema>
): NotificationTypeHandler {
  return {
    ...definition,
    buildNotificationPayload(data, ctx) {
      return definition.buildNotificationPayload
        ? definition.buildNotificationPayload(data as z.output<TSchema>, ctx)
        : { metadata: data };
    },
  };
}

export function defineEmailNotificationHandler<
  TSchema extends z.ZodType,
  TEmailEvent extends EmailEvent,
>(
  definition: NotificationHandlerDefinition<TSchema> & {
    buildEmailPayload: (
      data: z.output<TSchema>,
      recipient: EmailRecipientInfo,
      ctx: EmailHandlerCtx
    ) => TEmailEvent;
  }
): EmailNotificationTypeHandler {
  const handler = defineNotificationHandler(definition);
  return {
    ...handler,
    buildEmailPayload(data, recipient, ctx) {
      return definition.buildEmailPayload(
        data as z.output<TSchema>,
        recipient,
        ctx
      );
    },
  };
}
